import * as AllAtoms from './atoms'
import { selectorFamily, selector } from 'recoil'
import { GraphQlEnum } from '../utils/Enums'
import { FilterOrdersByBiltyState, checkDriverStatus, getUnloadingTimestamp } from '../utils/Functions'
import moment from 'moment'

/* ============================================================================================== */
/* ===================================== Bilty Management ======================================= */
/* ============================================================================================== */

export const biltyManagementSelector = selectorFamily({
	key: 'biltyManagementSelector',
	get:
		(props) =>
		({ get }) => {
			const { segment, tabStatus, showMissingKanta } = props

			let MappedOrders = []
			const user = get(AllAtoms.userAtom)
			const jobs = get(AllAtoms[GraphQlEnum[segment]?.fetch?.['alternate']?.atom])
			let orders = get(AllAtoms[GraphQlEnum[segment]?.fetch?.['normal']?.atom])

			if (user?.UserType === 'BaseManager') {
				orders = orderByBaseFunction({ orders, user, segment })
			}
			if (tabStatus === 'issuance') {
				MappedOrders = singleOrderFunction({ orders, segment })
				MappedOrders = [...MappedOrders]?.sort?.((a, b) => parseInt(b?.orderNumber) - parseInt(a?.orderNumber))
			} else if (['inProgress', 'completed'].includes(tabStatus)) {
				MappedOrders = get(groupedByJobSelector({ orders, segment }))
				MappedOrders = [...MappedOrders]
					?.filter(
						(job) =>
							tabStatus !== 'inProgress' ||
							!showMissingKanta ||
							!job?.bilty?.every((bilty) => bilty?.initialKantaWeight) ||
							job?.bilty?.every?.((bilty) => bilty?.initialKantaWeight) === 0
					)
					?.sort((a, b) => parseInt(b?.jobNumber) - parseInt(a?.jobNumber))
			} else if (tabStatus === 'cancelled') {
				MappedOrders = get(cancelledJobSelector({ jobs, segment }))
				MappedOrders = [...MappedOrders]?.sort((a, b) => parseInt(b?.bilty?.[0]?.biltyCancelledAt) - parseInt(a?.bilty?.[0]?.biltyCancelledAt))
			}
			return MappedOrders
		},
})

export const biltyTabSelector = selectorFamily({
	key: 'biltyTabSelector',
	get:
		(props) =>
		({ get }) => {
			const { segment, tabStatus, showMissingKanta } = props
			const MappedOrders = get(biltyManagementSelector({ segment, tabStatus, showMissingKanta }))
			const GroupMappedJobs = tabStatus !== 'cancelled' ? FilterOrdersByBiltyState([...MappedOrders], tabStatus) : MappedOrders
			return GroupMappedJobs
		},
})

export const groupedByJobSelector = selectorFamily({
	key: 'groupedByJobSelector',
	get:
		(props) =>
		({ get }) => {
			const { orders, segment } = props
			let movements = {}
			let movedCount = {}
			orders.forEach((order) => {
				const parentOrder = order?.ParentOrder?.[GraphQlEnum[segment]?.parentType]
				const parentMovements = order?.ParentOrder?.Movements?.filter(
					(movement) =>
						movement?.subMovementType !== 'ImportShortHaul' &&
						movement?.subMovementType !== 'ExportShortHaul' &&
						movement?.subMovementType !== 'ImportDoubleTwentyShortHaul' &&
						movement?.subMovementType !== 'ExportDoubleTwentyShortHaul'
				)
				parentMovements?.forEach((movement) => {
					const childOrder = movement?.ChildOrder?.[GraphQlEnum[segment]?.childType]

					movedCount[childOrder?._id] = (movedCount[childOrder?._id] || 0) + (movement?.status === 'PartialMatched' ? movement?.count : 0)
					if (movement.Job) {
						movements[`${movement?.Job?._id}`] = {
							...movement?.Job,
							matched: movement?.status,
							type: order?.type,
							subOrders: [
								...(movements?.[`${movement?.Job?._id}`]?.subOrders || []),
								{
									...childOrder,
									...(['Import', 'Export'].includes(segment) && { documentNumber: parentOrder?.documentNumber, shippingLine: parentOrder?.shippingLine }),
									...(segment === 'Export' && { vesselCutoffDT: parentOrder?.vesselCutoffDT }),
									preferences: parentOrder?.preferences,
									clearingAgents: parentOrder?.clearingAgents,
									businessName: order?.Corporate?.businessName,
									businessCode: order?.Corporate?.businessCode,
									movementType: movement?.movementType,
									partialMatched: movement?.partialMatch,
									completeMatched: !movement?.partialMatch && !!movement?.totalCount,
									movementCount: !movement?.partialMatch && !!movement?.totalCount ? movement?.totalCount : movement?.count,
									movedCount: movedCount[childOrder?._id],
									totalCount: movement?.totalCount,
									movedUnits: movement?.movedUnits,
									orderCategory:
										'ImportShifting' === movement?.movementType
											? parentOrder?.preferences?.numberFlatbeds === parentOrder?.[GraphQlEnum[segment]?.childrenType]?.length
												? 'Shifting'
												: 'Destuffing'
											: movement?.movementType,
									type: order?.type,
								},
							],
						}
					}
				})
			})
			return Object.values(movements)?.map((job) => get(singleJobSelector({ order: job, segment })))
		},
})

export const singleJobSelector = selectorFamily({
	key: 'singleJobSelector',
	get:
		(props) =>
		({ get }) => {
			const { order, segment } = props
			let journeyLocations = {}

			const allBiltyOps = get(AllAtoms.biltyOpsAtom)
			const biltyCreatedByName = allBiltyOps.find((biltyOps) => biltyOps?._id === order?.bilty?.[0]?.biltyCreatedBy)
			const biltyCompletedByName = allBiltyOps.find((biltyOps) => biltyOps?._id === order?.bilty?.[0]?.biltyCompletedBy)

			order?.Journeys?.forEach((journey) => {
				journey?.steps === 1 &&
					(journeyLocations[journey?.fromOrderLocation?.locationType] = [
						...(journeyLocations?.[journey?.fromOrderLocation?.locationType] || []),
						{ Location: journey?.fromOrderLocation?.Location, contacts: journey?.fromOrderLocation?.contacts },
					])
				journeyLocations[journey?.toOrderLocation?.locationType] = [
					...(journeyLocations?.[journey?.toOrderLocation?.locationType] || []),
					{ Location: journey?.toOrderLocation?.Location, contacts: journey?.toOrderLocation?.contacts },
				]
			})
			return {
				...order,
				Broker: order?.Broker,
				Driver: order?.Driver,
				Supplier: order?.Supplier?.User,
				Tracker: order?.Tracker,
				driverPhone: order?.Driver?.phoneNumber,
				driverVehicleRegistration: order?.Vehicle?.registrationNumber?.toLowerCase(),
				subOrderNumber: order?.subOrders?.map((value) => value?.subOrderNumber),
				movementType: [...new Set(order?.subOrders?.map((value) => value?.movementType))],
				orderCategory: order?.subOrders?.map((value) => value?.orderCategory),
				businessName: [...new Set(order?.subOrders?.map((value) => value?.businessName))],
				bilty: order?.bilty?.filter((value) => value?._id),
				biltyNumber: [...new Set(order?.bilty?.map((value) => value?.biltyNumber))],
				biltyCreatedByName: biltyCreatedByName?.User,
				biltyCompletedByName: biltyCompletedByName?.User,
				loadingDT: order?.subOrders?.map((value) => value?.loadingDT),
				invoiceNumber: order?.subOrders?.map((value) => value?.Invoice?.invoiceNumber),
				journeyLocations: journeyLocations,
				...(segment === 'Export' && {
					vesselCutoffDT: [...new Set(order?.subOrders?.map((value) => value?.vesselCutoffDT))]?.filter((value) => value),
				}),
				...(segment === 'Import' && {
					freeDays: [...new Set(order?.subOrders?.map((value) => value?.freeDays))]?.filter((value) => value),
				}),

				...(['Import', 'Export'].includes(segment) && {
					shippingLine: [...new Set(order?.subOrders?.map((value) => value?.shippingLine)?.flat())],
					clearingAgents: [...new Set(order?.subOrders?.map((value) => value?.clearingAgents)?.flat())],
					documentNumber: [...new Set(order?.subOrders?.map((value) => value?.documentNumber))],
					containerNumber: order?.subOrders?.map((value) => value?.containerNumber),
				}),
			}
		},
})

export const singleOrderFunction = (props) => {
	const { orders, segment } = props
	let movedCount = {}
	const mappedOrders = {}
	orders?.forEach((order) => {
		const corporate = order?.Corporate
		const parentOrder = order?.ParentOrder?.[GraphQlEnum[segment]?.parentType]
		const preferences = parentOrder?.preferences
		const parentMovements = order?.ParentOrder?.Movements?.filter(
			(movement) =>
				movement?.subMovementType !== 'ImportShortHaul' &&
				movement?.subMovementType !== 'ExportShortHaul' &&
				movement?.subMovementType !== 'ImportDoubleTwentyShortHaul' &&
				movement?.subMovementType !== 'ExportDoubleTwentyShortHaul' &&
				!preferences?.lotOrder
		)

		parentMovements.forEach((movement) => {
			const orderLocations = {}
			const childOrder = movement?.ChildOrder?.[GraphQlEnum[segment]?.childType]

			movedCount[childOrder?._id] = (movedCount[childOrder?._id] || 0) + (movement?.status === 'PartialMatched' ? movement?.count : 0)

			const sortedOrderLocations = [...childOrder?.OrderLocations]?.sort((a, b) => a?.steps - b?.steps)
			sortedOrderLocations?.forEach((location) => {
				orderLocations[location?.locationType] = [...(orderLocations?.[location?.locationType] || []), { Location: location?.Location }]
			})

			const orderCategory =
				'ImportShifting' === movement?.movementType
					? preferences?.numberFlatbeds === parentOrder?.[GraphQlEnum[segment]?.childrenType]?.length
						? 'Shifting'
						: 'Destuffing'
					: movement?.movementType

			mappedOrders[`${childOrder?._id}`] = {
				...childOrder,
				...(['Import', 'Export'].includes(segment) && { documentNumber: parentOrder?.documentNumber, shippingLine: parentOrder?.shippingLine }),
				...(segment === 'Export' && { vesselCutoffDT: parentOrder?.vesselCutoffDT }),
				...(segment === 'LongHaul' && {
					shipmentInfo: childOrder?.shipmentInfo,
					vehicleInfo: { vehicleType: childOrder?.vehicleType, vehicleSubType: childOrder?.vehicleSubType },
				}),
				orderNumber: parentOrder?.orderNumber,
				ChildId: childOrder?._id,
				ParentId: parentOrder?._id,
				orderCategory: orderCategory,
				movementType: movement?.movementType,
				subMovementType: movement?.subMovementType,
				businessName: corporate?.businessName,
				businessCode: corporate?.businessCode,
				clearingAgents: parentOrder?.clearingAgents,
				orderLocations: orderLocations,
				preferences: parentOrder?.preferences,
				totallyMoved: movement?.totalCount === movedCount[childOrder?._id],
				movementCount: movement?.count,
				movedCount: movedCount[childOrder?._id],
				totalCount: movement?.totalCount,
				movedUnits: movement?.movedUnits,
				type: order?.type,
				movements: [...(mappedOrders[childOrder?._id]?.movements || []), movement],
			}
		})
	})
	return Object?.values(mappedOrders)
}

export const cancelledJobSelector = selectorFamily({
	key: 'cancelledJobSelector',
	get:
		(props) =>
		({ get }) => {
			const { jobs } = props
			const allBiltyOps = get(AllAtoms.biltyOpsAtom)

			const cancelledJobs = jobs
				?.filter((job) => job?.bilty?.[0]?.isCancelled)
				?.map((job) => {
					let journeyLocations = {}
					job?.Journeys?.forEach((journey) => {
						journey?.steps === 1 &&
							(journeyLocations[journey?.fromOrderLocation?.locationType] = [
								...(journeyLocations?.[journey?.fromOrderLocation?.locationType] || []),
								{ Location: journey?.fromOrderLocation?.Location, contacts: journey?.fromOrderLocation?.contacts },
							])
						journeyLocations[journey?.toOrderLocation?.locationType] = [
							...(journeyLocations?.[journey?.toOrderLocation?.locationType] || []),
							{ Location: journey?.toOrderLocation?.Location, contacts: journey?.toOrderLocation?.contacts },
						]
					})
					return {
						...job,
						Broker: job?.Broker,
						journeyLocations: journeyLocations,
						driverPhone: job?.Driver?.phoneNumber,
						driverVehicleRegistration: job?.Vehicle?.registrationNumber?.toLowerCase(),
						bilty: job?.bilty,
						biltyNumber: [...new Set(job?.bilty?.map((value) => value?.biltyNumber))],
						biltyCreatedByName: allBiltyOps.find((biltyOps) => biltyOps?._id === job?.bilty?.[0]?.biltyCreatedBy)?.User,
						biltyCancelledByName: allBiltyOps.find((biltyOps) => biltyOps?._id === job?.bilty?.[0]?.biltyCancelledBy)?.User,
					}
				})

			return cancelledJobs
		},
})

export const dataByIDSelector = selectorFamily({
	key: 'dataByIDSelector',
	get:
		(props) =>
		({ get }) => {
			const { segment, id, tabStatus, collection } = props
			if (collection?.includes('biltyManagement')) {
				return get(biltyManagementSelector({ segment, tabStatus })).filter((order) => id?.includes(order?._id))
			} else if (collection === 'biltyTracking') {
				return get(biltyTrackingSelector({ segment })).filter((order) => id?.includes(order?._id))
			} else if (collection === 'userManagement') {
				return get(biltyOpsSelector).filter((user) => id?.includes(user?._id))
			} else if (collection === 'trackerManagement') {
				return get(trackersSelector())?.filter((tracker) => id?.includes(tracker?._id))
			}
		},
})

/* ============================================================================================== */
/* ====================================== Bilty Tracking ======================================== */
/* ============================================================================================== */

export const biltyTrackingSelector = selectorFamily({
	key: 'biltyTrackingSelector',
	get:
		(props) =>
		({ get }) => {
			const { segment, showLateBiltyOrders } = props
			let MappedOrders = get(groupedByJobSelector({ orders: get(AllAtoms[GraphQlEnum[segment]?.fetch?.['normal']?.atom]), segment }))

			let FilteredOrders = MappedOrders?.filter((mappedOrder) => {
				return (
					mappedOrder?.bilty?.every((bilty) => bilty?.biltyStatus !== 'completed') &&
					checkDriverStatus({
						journeys: mappedOrder?.Journeys,
						status: 'driverReachedDropoffLocation',
					})
				)
			})
			if (showLateBiltyOrders) {
				const current = moment().startOf('day')
				FilteredOrders = FilteredOrders?.filter((order) => {
					const unloadingCompletedTS = getUnloadingTimestamp({
						journeys: order?.Journeys,
						status: 'driverReachedDropoffLocation',
						driverSubStatus: 'unloadingCompleted',
					})
					const unloadingDaysDifference = unloadingCompletedTS && moment(unloadingCompletedTS)?.diff(moment(current), 'days')
					return parseFloat(unloadingDaysDifference) < -2
				})
			}
			return FilteredOrders
		},
})

/* ============================================================================================== */
/* ====================================== User Management ======================================= */
/* ============================================================================================== */

export const authSelector = selectorFamily({
	key: 'authSelector',
	get:
		(props) =>
		({ get }) => {
			const user = get(AllAtoms.userAtom)
			const authorized = ['BiltyAdmin', 'BaseManager', 'BiltySuperAdmin']?.includes(user?.userType)
			return user ? { user: user?.userType ? user : 'no user', authorized } : undefined
		},
})

export const biltyOpsSelector = selector({
	key: 'biltyOpsSelector',
	get: ({ get }) => {
		return singleUserFunction({ users: get(AllAtoms.biltyOpsAtom) })
	},
})

export const biltyOpsObjectSelector = selector({
	key: 'biltyOpsObjectSelector',
	get: ({ get }) => {
		const biltyOps = {}
		get(AllAtoms.biltyOpsAtom)?.forEach((biltyOp) => {
			biltyOps[biltyOp?._id] = { firstName: biltyOp?.User?.firstName, lastName: biltyOp?.User?.lastName }
		})
		return biltyOps
	},
})

export const trackersSelector = selectorFamily({
	key: 'trackersSelector',
	get:
		(props) =>
		({ get }) => {
			const { showActiveTrackers, showConnectedTrackers } = props
			const jobs = get(AllAtoms.trackerJobAtom)
			const trackers = get(AllAtoms.trackersAtom)?.reduce((trackers, tracker) => {
				if ((!showActiveTrackers || tracker?.status === 'active') && (!showConnectedTrackers || tracker?.isConnected)) {
					trackers[tracker?._id] = tracker
				}
				return trackers
			}, {})
			jobs?.forEach((job, index) => {
				if (job?.Tracker && trackers[job?.Tracker?._id] && trackers[job?.Tracker?._id]?.isConnected) {
					trackers[job?.Tracker?._id] = {
						...trackers[job?.Tracker?._id],
						installationDate: job?.driverMatchedAt,
						lane: { _id: job?.Lane?._id, from: job?.Lane?.from?.name, to: job?.Lane?.to?.name },
						jobStatus: job?.status,
						vehicle: job?.Vehicle?.registrationNumber,
						driver: job?.Driver?.phoneNumber,
					}
				}
			})

			return Object.values(trackers)
		},
})

export const singleUserFunction = (props) => {
	const { users } = props
	return users?.map((user) => ({
		_id: user?._id,
		userId: user?.User?._id,
		userName: user?.User?.firstName + ' ' + user?.User?.lastName,
		phoneNumber: user?.User?.phoneNumber,
		email: user?.User?.email,
		userTypes: user?.User?.UserType,
		biltyUserTypeId: user?.User?.UserType?.find((value) => ['BiltySuperAdmin', 'BiltyAdmin', 'BaseManager', 'User'].includes(value?.userType))?._id,
		regions: user?.Regions?.map((region) => ({ label: region?.name, value: region?._id })),
	}))
}

export const userTypesOptionsSelector = selector({
	key: 'userTypesOptionsSelector',
	get: ({ get }) => {
		const userTypes = get(AllAtoms?.userTypesAtom)?.filter((value) => ['BaseManager', 'BiltySuperAdmin', 'BiltyAdmin', 'User']?.includes(value?.userType))
		let options = userTypes?.map((userType) => {
			return {
				value: userType?.userType,
				key: userType?._id,
			}
		})
		return options
	},
})

export const orderByBaseFunction = (props) => {
	const { orders, user, segment } = props
	const userRegions = user?.regions?.map((region) => region?._id)

	return orders?.map((order) => {
		return {
			...order,
			ParentOrder: {
				...order?.ParentOrder,
				Movements: order?.ParentOrder?.Movements?.filter((movement) => {
					const childOrder = movement?.ChildOrder?.[GraphQlEnum[segment]?.childType]
					const orderRegionIds = [
						...new Set(
							childOrder?.OrderLocations.filter((location) => ['loading', 'dropoff'].includes(location?.locationType))?.map((filteredLocation) => {
								return filteredLocation?.Location?.Region?.ParentRegion?._id
							})
						),
					]
					return orderRegionIds?.some((orderRegion) => userRegions?.includes(orderRegion))
				}),
			},
		}
	})
}

/* ============================================================================================== */
/* ==================================== Tracking and Regions ==================================== */
/* ============================================================================================== */

export const trackersOptionsSelector = selector({
	key: 'trackersOptionsSelector',
	get: ({ get }) => {
		const trackers = get(AllAtoms.trackersAtom)?.filter((value) => !value?.isConnected && value?.status === 'active')

		let options = trackers
			?.map((tracker) => {
				return {
					value: tracker?.name,
					key: tracker?._id,
				}
			})
			?.filter((tracker) => tracker?.value)
		return options
	},
})

export const vicinitiesOptionsSelector = selector({
	key: 'vicinitiesOptionsSelector',
	get: ({ get }) => {
		const vicinities = get(AllAtoms.vicinitiesAtom)?.filter((vicinity) => !vicinity?.ParentRegion)
		let options = vicinities?.map((vicinity) => {
			return {
				label: vicinity?.name,
				value: vicinity?._id,
			}
		})
		return options
	},
})
